/**
 * Parse transcript JSON in either the legacy v1 or whisperX v2 format.
 */
export function parseTranscript(data) {
  if (!data) {
    throw new Error('No transcript data provided');
  }

  if (data.version === 2) {
    return {
      version: 2,
      segments: data.segments || [],
      chapters: []
    };
  }

  return {
    version: 1,
    segments: data.diarization?.segments || [],
    chapters: data.chapters || []
  };
}

export function extractSpeakers(segments) {
  const speakers = new Set();
  segments.forEach((segment) => {
    if (segment.speaker && segment.speaker !== 'SPEAKER_UNKNOWN') {
      speakers.add(segment.speaker);
    }
  });
  return Array.from(speakers).sort();
}

export function findActiveSegment(segments, currentTime) {
  return segments.find((segment) =>
    currentTime >= segment.start && currentTime <= segment.end
  );
}

export function findActiveWord(segment, currentTime) {
  if (!segment?.words?.length) {
    return null;
  }

  return segment.words.find((word) =>
    currentTime >= word.start && currentTime <= word.end
  );
}

export function filterTranscriptSegments(segments, query) {
  const normalizedQuery = query.trim().toLowerCase();
  if (!normalizedQuery) {
    return segments;
  }

  return segments.filter((segment) =>
    (segment.text || '').toLowerCase().includes(normalizedQuery)
  );
}

const DEFAULT_PARAGRAPH_MAX_WORDS = 120;
const DEFAULT_PARAGRAPH_MAX_DURATION = 60;
const DEFAULT_PARAGRAPH_MAX_GAP = 2.5;

function getNumericTime(value) {
  const time = Number(value);
  return Number.isFinite(time) ? time : null;
}

function getSegmentWordCount(segment) {
  if (Array.isArray(segment.words) && segment.words.length > 0) {
    return segment.words.length;
  }

  return (segment.text || '').trim().split(/\s+/).filter(Boolean).length;
}

/**
 * Combine short timestamped segments into readable paragraphs without
 * changing the original segment data used for playback synchronization.
 */
export function groupTranscriptSegments(
  segments,
  {
    maxWords = DEFAULT_PARAGRAPH_MAX_WORDS,
    maxDuration = DEFAULT_PARAGRAPH_MAX_DURATION,
    maxGap = DEFAULT_PARAGRAPH_MAX_GAP
  } = {}
) {
  if (!Array.isArray(segments) || segments.length === 0) {
    return [];
  }

  const paragraphs = [];
  let currentSegments = [];
  let currentWordCount = 0;

  const addParagraph = () => {
    if (currentSegments.length === 0) {
      return;
    }

    const firstSegment = currentSegments[0];
    const lastSegment = currentSegments[currentSegments.length - 1];
    paragraphs.push({
      start: firstSegment.start,
      end: lastSegment.end,
      segments: currentSegments
    });
    currentSegments = [];
    currentWordCount = 0;
  };

  segments.forEach((segment) => {
    const previousSegment = currentSegments[currentSegments.length - 1];
    const segmentWordCount = getSegmentWordCount(segment);
    const segmentStart = getNumericTime(segment.start);
    const previousEnd = getNumericTime(previousSegment?.end);
    const paragraphStart = getNumericTime(currentSegments[0]?.start);
    const gap = segmentStart != null && previousEnd != null
      ? segmentStart - previousEnd
      : 0;
    const duration = segmentStart != null && paragraphStart != null
      ? segmentStart - paragraphStart
      : 0;
    const exceedsWordLimit = currentWordCount > 0
      && currentWordCount + segmentWordCount > maxWords;
    const exceedsDurationLimit = currentSegments.length > 0
      && duration >= maxDuration;
    const hasLongPause = currentSegments.length > 0 && gap >= maxGap;

    if (exceedsWordLimit || exceedsDurationLimit || hasLongPause) {
      addParagraph();
    }

    currentSegments.push(segment);
    currentWordCount += segmentWordCount;
  });

  addParagraph();
  return paragraphs;
}
